import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, PenNib, FileText, ShieldCheck, Calendar, IdentificationBadge, Globe } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API } from '@/config/api';

export default function SignAgreement() {
  const { recruiterId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signResult, setSignResult] = useState(null);
  const [signatureText, setSignatureText] = useState('');
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/agreement/${recruiterId}?token=${token || ''}`).then(res => {
      setAgreement(res.data);
      if (res.data.status === 'agreement_signed' || res.data.status === 'active') {
        // Load the certificate
        axios.get(`${API}/agreement/${recruiterId}/certificate?token=${token || ''}`).then(certRes => {
          setSignResult(certRes.data);
        }).catch(() => {});
      }
    }).catch(() => toast.error('Failed to load agreement')).finally(() => setLoading(false));
  }, [recruiterId, token]);

  const handleSign = async () => {
    if (!signatureText.trim()) { toast.error('Please type your full name as signature'); return; }
    if (!agreed) { toast.error('Please agree to the terms'); return; }
    setSigning(true);
    try {
      const { data } = await axios.post(`${API}/agreement/${recruiterId}/sign?token=${token || ''}`, { signature_text: signatureText, agreed });
      setSignResult(data);
      toast.success('Agreement signed successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-pulse text-gray-400">Loading agreement...</div></div>;
  }

  // Signed Certificate View
  if (signResult) {
    return (
      <div className="min-h-screen bg-gray-50 py-12" data-testid="agreement-signed-success">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {/* Success Banner */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <ShieldCheck size={32} weight="fill" className="text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              Agreement Signed Successfully
            </h1>
            <p className="text-gray-500 mt-2">A confirmation has been sent to your email</p>
          </div>

          {/* Signing Certificate */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="signing-certificate">
            <div className="bg-blue-800 px-8 py-5 flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-medium">Digital Signing Certificate</p>
                <p className="text-gray-400 text-xs mt-0.5">Study Alliance Partnership Agreement</p>
              </div>
              <ShieldCheck size={28} className="text-blue-300" />
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div className="flex items-start gap-3">
                  <IdentificationBadge size={18} className="text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Agreement Number</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5 font-mono">{signResult.agreement_number}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <ShieldCheck size={18} className="text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Certificate ID</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5 font-mono">{signResult.certificate_id}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar size={18} className="text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Signed Date</p>
                    <p className="text-sm text-gray-900 mt-0.5">{signResult.signed_at ? new Date(signResult.signed_at).toLocaleString() : '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Globe size={18} className="text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Signing IP</p>
                    <p className="text-sm text-gray-900 mt-0.5 font-mono">{signResult.signing_ip || 'Recorded'}</p>
                  </div>
                </div>
              </div>

              <Separator className="my-6" />

              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Signer</p>
                  <p className="text-gray-900 font-medium">{signResult.signer_name || signResult.recruiter_name}</p>
                  <p className="text-sm text-gray-500">{signResult.company_name || agreement?.company_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Digital Signature</p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 inline-block">
                    <p className="text-2xl text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif", fontStyle: 'italic' }}>
                      {signResult.signer_name || signResult.signature_text || signatureText}
                    </p>
                  </div>
                </div>
              </div>

              <Separator className="my-6" />

              <div className="flex items-center gap-2 text-xs text-gray-400">
                <ShieldCheck size={14} className="text-green-500" />
                <span>This document was electronically signed and the signing event has been recorded with a unique certificate ID, timestamp, and IP address for verification purposes.</span>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-500 text-sm">Our team will review your agreement and activate your recruiter portal. You will receive login credentials via email.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="sign-agreement-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <FileText size={36} className="text-gray-700 mx-auto mb-3" />
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>Recruiter Partnership Agreement</h1>
          <p className="text-gray-500 mt-2">Study Alliance - Recruitment Partner Agreement</p>
          {agreement?.agreement_number && (
            <p className="text-xs text-gray-400 mt-1 font-mono">{agreement.agreement_number}</p>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          {/* Agreement Info Bar */}
          <div className="bg-gray-50 border-b border-gray-200 px-8 py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <span><strong>Recruiter:</strong> {agreement?.recruiter_name}</span>
              <span className="hidden sm:inline"><strong>Company:</strong> {agreement?.company_name}</span>
            </div>
            <div className="text-xs text-gray-400">
              {agreement?.issued_date && `Issued: ${new Date(agreement.issued_date).toLocaleDateString()}`}
            </div>
          </div>

          {/* Agreement Content */}
          <div className="p-8 border-b border-gray-200">
            <div className="prose prose-sm max-w-none text-gray-600">
              <p className="mb-4">This Recruitment Partnership Agreement ("Agreement") is entered into between <strong>Study Alliance Inc.</strong> ("Company") and <strong>{agreement?.recruiter_name}</strong> representing <strong>{agreement?.company_name}</strong> ("Recruiter"), effective as of the date of electronic signature below.</p>

              <h3 className="text-gray-900 font-semibold text-base mt-6 mb-3" style={{ fontFamily: "'Libre Baskerville', serif" }}>1. Scope of Partnership</h3>
              <p>The Recruiter agrees to identify, counsel, and refer prospective students to Study Alliance for college admission and enrollment services. Study Alliance will provide platform access, application support, and institutional placement services for all referred students.</p>

              <h3 className="text-gray-900 font-semibold text-base mt-6 mb-3" style={{ fontFamily: "'Libre Baskerville', serif" }}>2. Recruiter Obligations</h3>
              <p>The Recruiter shall:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Provide accurate and complete student information through the designated portal</li>
                <li>Submit all required student documents in a timely manner</li>
                <li>Comply with all applicable education regulations and laws</li>
                <li>Maintain strict confidentiality of all student data and proprietary information</li>
                <li>Not misrepresent Study Alliance's services, partnerships, or capabilities</li>
                <li>Conduct all recruitment activities in an ethical and professional manner</li>
              </ul>

              <h3 className="text-gray-900 font-semibold text-base mt-6 mb-3" style={{ fontFamily: "'Libre Baskerville', serif" }}>3. Company Obligations</h3>
              <p>Study Alliance shall:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Provide secure portal access for student submissions and tracking</li>
                <li>Process student applications in a timely and professional manner</li>
                <li>Provide regular status updates on all submitted applications</li>
                <li>Pay agreed commissions for successful student placements per the commission schedule</li>
                <li>Provide training materials and onboarding support to the Recruiter</li>
              </ul>

              <h3 className="text-gray-900 font-semibold text-base mt-6 mb-3" style={{ fontFamily: "'Libre Baskerville', serif" }}>4. Commission & Payments</h3>
              <p>Commission rates and payment terms shall be as outlined in the separate Commission Schedule provided upon account activation. Commissions are payable upon successful enrollment confirmation of referred students.</p>

              <h3 className="text-gray-900 font-semibold text-base mt-6 mb-3" style={{ fontFamily: "'Libre Baskerville', serif" }}>5. Confidentiality & Data Protection</h3>
              <p>Both parties agree to maintain strict confidentiality regarding student information, business processes, commercial terms, and any proprietary data exchanged during the course of this partnership. Both parties shall comply with applicable data protection regulations.</p>

              <h3 className="text-gray-900 font-semibold text-base mt-6 mb-3" style={{ fontFamily: "'Libre Baskerville', serif" }}>6. Term and Termination</h3>
              <p>This Agreement shall remain in effect for twelve (12) months from the date of signing and shall automatically renew for successive 12-month periods unless either party provides 30 days written notice of termination. Either party may terminate this Agreement immediately in the event of a material breach.</p>

              <h3 className="text-gray-900 font-semibold text-base mt-6 mb-3" style={{ fontFamily: "'Libre Baskerville', serif" }}>7. Governing Law</h3>
              <p>This Agreement shall be governed by and construed in accordance with the laws of the jurisdiction in which Study Alliance Inc. operates.</p>
            </div>
          </div>

          {/* Signing Section */}
          <div className="p-8 bg-gray-50">
            <div className="flex items-center gap-3 mb-6">
              <PenNib size={22} className="text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Electronic Signature</h3>
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Type your full legal name as your digital signature</Label>
                <Input
                  value={signatureText}
                  onChange={e => setSignatureText(e.target.value)}
                  placeholder="Enter your full legal name"
                  className="text-lg h-12 bg-white"
                  style={{ fontFamily: "'Libre Baskerville', serif", fontStyle: 'italic' }}
                  data-testid="signature-input"
                />
                {signatureText && (
                  <div className="mt-3 bg-white border border-gray-200 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Signature Preview</p>
                    <p className="text-2xl text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif", fontStyle: 'italic' }}>
                      {signatureText}
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-lg">
                <Checkbox checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" data-testid="agree-terms-checkbox" />
                <Label className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                  I, <strong>{signatureText || '[your name]'}</strong>, have read, understood, and agree to all terms and conditions set forth in this Recruitment Partnership Agreement. I acknowledge that this electronic signature carries the same legal validity as a handwritten signature.
                </Label>
              </div>

              <Button onClick={handleSign} disabled={signing || !signatureText || !agreed} className="bg-blue-800 hover:bg-blue-700 text-white w-full h-12" data-testid="sign-agreement-btn">
                <PenNib size={18} className="mr-2" />
                {signing ? 'Processing Signature...' : 'Sign Agreement Electronically'}
              </Button>

              <p className="text-xs text-center text-gray-400 flex items-center justify-center gap-1.5">
                <ShieldCheck size={14} className="text-green-500" />
                Your signature will be recorded with a timestamp, IP address, and unique certificate ID for verification
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
